import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AddUser = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'booker'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post(`${API}/auth/register`, formData);
      toast.success('User added successfully');
      navigate('/');
    } catch (error) {
      console.error('Failed to add user:', error);
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button onClick={() => navigate('/')} className="btn-gold-outline" size="icon" data-testid="back-from-add-user-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold title-gold">Add New User</h2>
      </div>

      <Card className="beast-card">
        <CardHeader>
          <CardTitle className="title-gold">User Information</CardTitle>
          <CardDescription className="text-slate-300">Create a new user account with role-based access</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-slate-200">Full Name *</Label>
              <Input
                id="full_name"
                type="text"
                placeholder="e.g., John Smith"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="input-dark"
                data-testid="add-user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-200">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="input-dark"
                data-testid="add-user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-200">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="e.g., +1 555 123 4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="input-dark"
                data-testid="add-user-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-200">Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="input-dark"
                data-testid="add-user-password-input"
              />
              <p className="text-xs text-slate-400">Minimum 6 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-slate-200">Role *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="add-user-role-select" className="input-dark">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {/* Admin can create any role */}
                  {user?.role === 'admin' && (
                    <>
                      <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                      <SelectItem value="om" data-testid="role-option-om">OM (Operations Manager)</SelectItem>
                    </>
                  )}
                  {/* Both admin and OM can create leader and booker */}
                  <SelectItem value="leader" data-testid="role-option-leader">Team Leader / QA / Coordinator</SelectItem>
                  <SelectItem value="booker" data-testid="role-option-booker">Booker (Read-only)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-300 space-y-1 mt-2">
                {user?.role === 'admin' && (
                  <>
                    <p><strong className="title-gold">Admin:</strong> Full access - manage managers, users, and notes</p>
                    <p><strong className="title-gold">OM:</strong> Can view and add users (booker/leader only)</p>
                  </>
                )}
                <p><strong className="title-gold">Leader:</strong> Can add notes</p>
                <p><strong className="title-gold">Booker:</strong> Read-only access to managers and notes</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 btn-gold-outline"
                disabled={loading}
                data-testid="cancel-add-user-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-gold"
                disabled={loading}
                data-testid="submit-add-user-btn"
              >
                {loading ? 'Adding...' : 'Add User'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddUser;